import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Navigate } from "react-router-dom";

import { RejectedPage } from "@/components/onboarding/RejectedPage";
import { useBusinessNavAccess } from "@/app/navigation/useBusinessNavAccess";
import { STAFF_SETUP_ALLOWED_STATUSES } from "@/app/org-access-policy";
import { useMyOrg, useOnboardingState } from "@/hooks/use-my-org";

function CenteredLoading({ label }: { label: string }) {
  return (
    <Box
      aria-label={label}
      role="status"
      sx={{ display: "grid", minHeight: "60vh", placeItems: "center" }}
    >
      <CircularProgress size={36} />
    </Box>
  );
}

function InlineError({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry?: () => void;
}) {
  return (
    <Box
      sx={{ display: "grid", minHeight: "60vh", placeItems: "center", p: 3 }}
    >
      <Paper
        role="alert"
        variant="outlined"
        sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}
      >
        <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
          <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 42 }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography color="text.secondary">{description}</Typography>
          {onRetry ? (
            <Button onClick={onRetry} variant="contained">
              Try again
            </Button>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}

export function OnboardingRouter() {
  const view = useMyOrg();
  const orgId = view.org?.org_id ?? null;
  // /orgs/me returns owner details only after canonical ownership admission,
  // including co-owners whose UID differs from the primary billing administrator.
  // Personal Firebase roles and staff-management permission are not ownership.
  const ownerDetails = typeof view.org?.owner_user_id === "string" && view.org.owner_user_id.length > 0;
  // Rejected owners receive minimal selector data; their existing recovery
  // screen still asks the owner-authorized endpoint, which remains authoritative.
  const ownerRecovery = view.kind === "rejected";
  const {
    data: onboardingState,
    isLoading: stateLoading,
    error: stateError,
    refetch: refetchState,
  } = useOnboardingState(!view.staffCapabilityPending && (ownerDetails || ownerRecovery) ? orgId : null);
  // Approved staff and owners land on their first navigation page (Home when
  // they may see it), resolved from the same definition the shell renders.
  const navigation = useBusinessNavAccess();
  const landing = navigation.landing;

  if (view.kind === "loading" || view.kind === "selection-required") {
    return <CenteredLoading label="Loading organization" />;
  }

  if (view.kind === "error") {
    return (
      <InlineError
        title="Unable to load your organization"
        description="We couldn't retrieve your organization details. Please try again."
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (view.kind === "no-org") {
    return <Navigate to="/getting-started" replace />;
  }

  if (view.staffCapabilityPending) {
    return <CenteredLoading label="Refreshing organization access" />;
  }

  if (!ownerDetails && !ownerRecovery) {
    if (typeof view.org.can_manage_staff === "boolean" && STAFF_SETUP_ALLOWED_STATUSES.has(view.org.status)) {
      // Inbox is always visible to staff, so a landing page always exists; each
      // destination's own guards and server readers still enforce access.
      if (!navigation.ready) {
        return <CenteredLoading label="Loading workspace" />;
      }
      return <Navigate to={landing ?? "/inquiries"} replace />;
    }
    return <InlineError title="Organization access unavailable"
      description="We couldn’t confirm access to this workspace. Choose another workspace or contact your organization." />;
  }

  if (stateLoading) {
    return <CenteredLoading label="Loading onboarding status" />;
  }

  if (stateError || !onboardingState) {
    return (
      <InlineError
        title="Unable to load onboarding status"
        description="We couldn't retrieve your application status. Please try again."
        onRetry={() => void refetchState()}
      />
    );
  }

  switch (onboardingState.display.view) {
    case "rejected":
      return <RejectedPage state={onboardingState} />;

    case "dashboard": {
      const status = view.org.status;
      if (status !== "approved" && status !== "active") {
        return <Navigate to="/getting-started" replace />;
      }
      if (!navigation.ready) {
        return <CenteredLoading label="Loading workspace" />;
      }
      return <Navigate to={landing ?? "/dashboard"} replace />;
    }

    default:
      return (
        <InlineError
          title="Unknown application state"
          description="Your application is in an unexpected state. Please contact support."
        />
      );
  }
}
